// src/components/guards/AvisoEmpresa.jsx
//
// EL AVISO DE PRIVACIDAD DEL EMPLEADO DE EMPRESA (EMPRESAS.md, T2.5)
//
// Quien entra porque su empresa le paga el programa ve esto ANTES que nada: qué
// ve su empresa de él y qué no ve nunca. `ProtectedRoute` lo pinta en lugar de
// la pantalla pedida hasta que lo acepta.
//
// El texto NO está aquí: llega del backend (`routers/legal.py`) con su versión,
// y esa versión es la que se acepta. Así la pantalla no puede enseñar un texto y
// guardar la firma de otro.
//
// «No estoy de acuerdo» no es un callejón: dice qué pasa y deja escribir a NSL o
// salir de la cuenta.

import { useState } from 'react';
import TextoLegal from '../legal/TextoLegal.jsx';
import { aceptaAviso } from './useAvisoEmpresa.js';
import './AvisoEmpresa.css';

const WHATSAPP = '5215610480746';

function temaGuardado() {
  try { return localStorage.getItem('theme') === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
}

export default function AvisoEmpresa({ datos, onAceptado, onRecarga }) {
  const doc = datos?.documento;
  const empresa = datos?.empresa || 'tu empresa';
  const [fase, setFase] = useState('leer');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [tema] = useState(temaGuardado);

  if (!doc) return null;

  async function acepta() {
    setEnviando(true);
    setError('');
    const r = await aceptaAviso(doc.version);
    setEnviando(false);
    if (r.ok) { onAceptado?.(); return; }
    if (r.status === 409) {
      // Cambió el texto mientras lo leía: se trae el nuevo y se le pide otra vez.
      setError('El aviso cambió mientras lo leías. Te mostramos el nuevo: léelo antes de aceptar.');
      onRecarga?.();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (r.status === 404) { onAceptado?.(); return; }   // ya no está en ningún programa
    setError(r.detalle || 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.');
  }

  function escribe() {
    // Sin emojis a propósito: WhatsApp los pinta como rombos rotos.
    const texto = `Hola, soy parte del programa de ${empresa} en NSL y tengo dudas sobre el aviso de privacidad.`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  }

  async function sale() {
    const t = localStorage.getItem('token');
    if (t) {
      try {
        await fetch('/api/logout', { method: 'POST', headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } });
      } catch { /* se limpia igual */ }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('nsl_sub_data');
    sessionStorage.removeItem('nsl_sub_data_at');
    window.location.assign('/login');
  }

  return (
    <div className="ave-raiz" data-tema={tema} data-prueba="aviso-empresa">
      <main className="ave-hoja" aria-labelledby="ave-titulo">
        <header className="ave-cabeza">
          <span className="ave-chip">Programa de {empresa}</span>
          <h1 id="ave-titulo">{doc.titulo}</h1>
          <p className="ave-resumen">{doc.resumen}</p>
        </header>

        {fase === 'leer' ? (
          <>
            <div className="ave-clausulas">
              {doc.clausulas.map((c, i) => (
                <section key={`${doc.version}-${i}`} className="ave-clausula" data-clausula={i + 1}>
                  <h2>{c.titulo}</h2>
                  <TextoLegal texto={c.texto} />
                </section>
              ))}
            </div>

            <footer className="ave-acciones">
              {error ? <p className="ave-error" role="alert">{error}</p> : null}
              <div className="ave-botones">
                <button type="button" className="ave-btn ave-btn--primario" onClick={acepta} disabled={enviando}>
                  {enviando ? 'Guardando…' : 'Acepto y continúo'}
                </button>
                <button type="button" className="ave-btn ave-btn--texto" onClick={() => setFase('rechazo')} disabled={enviando}>
                  No estoy de acuerdo
                </button>
              </div>
            </footer>
          </>
        ) : (
          <section className="ave-rechazo" data-prueba="aviso-rechazo" aria-live="polite">
            <h2>Sin tu aceptación no podemos darte acceso al programa</h2>
            <p>
              El programa de {empresa} incluye que tu empresa siga tu avance. Si no estás de acuerdo con
              lo que ve, no podemos abrirte el entrenamiento por ese programa.
            </p>
            <p>
              Si tienes dudas sobre lo que se comparte, escríbenos
              {datos?.correo ? <> a <a href={`mailto:${datos.correo}`}>{datos.correo}</a></> : null} o por
              WhatsApp, o háblalo con tu empresa.
            </p>
            <div className="ave-botones">
              <button type="button" className="ave-btn ave-btn--primario" onClick={() => setFase('leer')}>
                Volver a leer el aviso
              </button>
              <button type="button" className="ave-btn ave-btn--borde" onClick={escribe}>
                Escribir por WhatsApp
              </button>
              <button type="button" className="ave-btn ave-btn--texto" onClick={sale}>
                Cerrar sesión
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
